import React from "react";
import { DateTime } from "luxon";
import {
  utcToZonedTime,
  zonedTimeToUtc,
  format,
  getTimezoneOffset,
} from "date-fns-tz";
import { isValid, parseISO } from "date-fns";
import store from "../../store";

import TimezoneTip from "../../common/toolTip/timezoneTip";
import enAU from "date-fns/locale/en-AU";
import enGB from "date-fns/locale/en-GB";
import enNZ from "date-fns/locale/en-NZ";
// import enIN from "date-fns/locale/en-IN";
import { arSA } from "date-fns/locale";

import {
  COUNTRY_TIMEZONES,
  DEFAULT_COUNTRY,
  COUNTRY_SA,
  COUNTRY_AE,
} from "../constants";

const timeStyle = { float: "left", lineHeight: "20px" };
const locale = {
  AU: enAU,
  NZ: enNZ,
  GB: enGB,
  SA: arSA,
};

/**
 * Formats a given date-time in a specified timezone.
 * @param {string} dateTime - The date-time string to format.
 * @param {string} fmt - The format string.
 * @param {string} timezone - The target timezone.
 * @returns {string} - The formatted date-time string.
 */
export const formatInTimeZone = (
  dateTime,
  fmt = "yyyy-MM-dd kk:mm:ss xxx",
  timezone,
) => {
  if (!dateTime) {
    return "";
  }

  let parsedTime;

  // Try ISO parsing first
  try {
    parsedTime = parseISO(dateTime);
  } catch {
    parsedTime = new Date(dateTime); // fallback
  }

  // If parseISO or Date fallback fails
  if (!isValid(parsedTime)) {
    console.log("Invalid dateTime input:", dateTime);
    return "Invalid Date";
  }

  try {
    const zonedDate = utcToZonedTime(parsedTime, timezone);
    return format(zonedDate, fmt, { timeZone: timezone });
  } catch (err) {
    console.log("Timezone formatting error:", timezone, err);
    return "Invalid Date";
  }
};

/**
 * Converts a local date-time to UTC.
 * @param {string} date - The local date-time string.
 * @param {string} timeZone - The local timezone.
 * @returns {string} - The UTC date-time string in ISO format.
 */
export function convertTimeToUTC(date, timeZone) {
  const zoneTime = new Date(date);
  const utcTime = isValid(zoneTime) ? zonedTimeToUtc(zoneTime, timeZone) : "";
  const formatTime = isValid(utcTime) ? utcTime.toISOString() : ""; //2011-10-05T14:48:00.000Z
  return formatTime;
}

/**
 * Retrieves the locale configuration based on the user's country settings.
 * @returns {Object} - Locale configuration object.
 */
export function getLocale() {
  const country = store
    .getState()
    .getIn(["config", "accountConfig", "country", "code"]);
  return locale[country] || locale[DEFAULT_COUNTRY];
}

/**
 * Retrieves the timezone abbreviation based on the current or account-configured timezone.
 * @param {string} formatString - The format string for the timezone.
 * @returns {string} - The timezone abbreviation.
 */
export function getTimezoneAbbr(formatString = "zzz") {
  const date = new Date();
  const defaultCountryTimezone = store
    .getState()
    .getIn(["config", "accountConfig", "default_timezone", "value"]);
  const accountTimezone = store
    .getState()
    .getIn(["manageAccount", "accountInfo", "time_zone"]);
  const accountCountry = store
    .getState()
    .getIn(["manageAccount", "accountInfo", "country"]);
  const timezone = accountTimezone || defaultCountryTimezone;

  const tzDate = utcToZonedTime(date, timezone);
  let zoneString = format(tzDate, formatString, {
    timeZone: timezone,
    locale: getLocale(),
  });
  if ([COUNTRY_SA, COUNTRY_AE].includes(accountCountry)) {
    const countryTimezoneAbbr = store
      .getState()
      .getIn(["config", "accountConfig", "default_timezone", "code"]);
    zoneString = countryTimezoneAbbr;
  }
  return zoneString;
}

/**
 * Converts a UTC date-time string to a local time in the specified timezone.
 * Optionally includes a tooltip for the timezone.
 * @param {string} date - The UTC date-time string.
 * @param {string} timeZone - The target timezone.
 * @param {boolean} hasTimestampTip - Whether to include a tooltip.
 * @param {string} formatString - The format for the local time.
 * @returns {Array|string|JSX.Element} - Formatted time, zone abbreviation, and name, or JSX if tooltip is enabled.
 */
export function convertUTCToTime(
  date,
  timeZone,
  hasTimestampTip = false,
  formatString = "dd/MM/yyyy HH:mm:ss",
) {
  let locale = getLocale();
  const accountCountry = store
    .getState()
    .getIn(["manageAccount", "accountInfo", "country"]);
  const utcTime = new Date(date);
  const zoneTime = isValid(utcTime) ? utcToZonedTime(utcTime, timeZone) : "";
  if (!isValid(utcTime) || !isValid(zoneTime)) {
    return hasTimestampTip ? "" : ["", "", ""];
  }
  const formatTime = format(zoneTime, formatString, { timeZone });
  let zoneAbbr = format(zoneTime, "zzz", {
    timeZone,
    locale,
  });
  let zoneName = format(zoneTime, "zzzz", {
    timeZone,
    locale,
  });
  if ([COUNTRY_SA, COUNTRY_AE].includes(accountCountry)) {
    const countryTimezoneAbbr = store
      .getState()
      .getIn(["config", "accountConfig", "default_timezone", "code"]);
    const countryTimezoneName = store
      .getState()
      .getIn(["config", "accountConfig", "default_timezone", "title"]);
    zoneAbbr = countryTimezoneAbbr;
    zoneName = countryTimezoneName;
  }
  if (hasTimestampTip) {
    return (
      <div>
        <span style={timeStyle}>{formatTime}</span>
        <TimezoneTip zoneAbbr={zoneAbbr} zoneName={zoneName} />
      </div>
    );
  }
  return [formatTime, zoneAbbr, zoneName];
}

/**
 * Converts a local date to UTC format.
 *
 * @param {string|Date} date - The date to be converted.
 * @param {string} [format="yyyy-MM-dd'T'HH:mm:ssZZ"] - The desired UTC format.
 * @returns {string} - The UTC-formatted date string, or an empty string if the input is invalid.
 */
export function convertLocalToUTC(date, format = "yyyy-MM-dd'T'HH:mm:ssZZ") {
  const dateTime = DateTime.fromISO(date);
  if (!dateTime.isValid) {
    return "";
  }
  return dateTime.toUTC().toFormat(format);
}

/**
 * Returns a list of weekdays with labels and values.
 *
 * @returns {Array<{label: string, value: number}>} - An array of weekdays with their corresponding numeric values (1-7).
 */
export function getWeekList() {
  let weekdays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  return weekdays.map((item, index) => ({
    label: item,
    value: index + 1,
  }));
}

/**
 * Generates a list of 24 hours with AM/PM labels and values.
 *
 * @returns {Array<{label: string, value: number, badge: string}>} - An array of hour objects with formatted labels, numeric values (1-24), and timezone abbreviation badges.
 */
export function getHoursList() {
  // value : 1-24 label : 01:00-12:00 AM/PM
  let res = [];
  let hours, amPm;
  for (let i = 1; i < 25; i++) {
    amPm = i <= 12 ? "AM" : "PM";
    hours = i === 12 || i === 24 ? 12 : i % 12;
    if (hours < 10) {
      hours = "0" + hours;
    }
    res.push({
      label: hours + ":00 " + amPm,
      value: i,
      badge: getTimezoneAbbr(),
    });
  }
  return res;
}

/**
 * Formats a date into a specified string format.
 *
 * @param {string|Date} date - The date to format.
 * @param {string} [format="D MMM YYYY"] - The desired date format.
 * @returns {string} - The formatted date string, or an empty string if the input is invalid.
 */
export function formatDate(date, format = "d LLL yyyy") {
  return date && DateTime.fromISO(date).isValid
    ? DateTime.fromISO(date).toFormat(format)
    : "";
}

/**
 * Generates a timezone label based on a provided timezone string.
 *
 * @param {string} timezone - The timezone identifier (e.g., "America/New_York").
 * @returns {string} - The formatted timezone label, or an empty string if the input is invalid.
 */
export function timezoneLabel(timezone) {
  if (timezone) {
    var v = getTimezoneOffset(timezone) / 60 / 60 / 1000;
    var minutes = (v - Math.floor(v)) * 60;
    var label =
      "UTC " +
      (v > -1 ? "+" : "") +
      Math.floor(v) +
      (minutes > 0 ? ":" + minutes : "");

    var name = timezone.slice(timezone.indexOf("/") + 1);
    name = name.replace("_", " ");

    return label + " (" + name + " Time)";
  }
  return "";
}

/**
 * Calculates the last date of the month based on a given date string.
 *
 * @param {string} dateString - The date string in "MM-YYYY" format.
 * @returns {Date|null} - The last date of the month, or null if the input is invalid.
 */
export function getLastDateOfMonth(dateString) {
  if (!dateString) {
    return null;
  }
  // Split the input string into month and year parts
  const [month, year] = dateString.split("-");

  if (!year) {
    return null;
  }

  // Create a Date object for the first day of the next month
  const firstDayOfNextMonth = new Date(year, month - 1 + 1, 1);

  // Subtract one day to get the last day of the current month
  const lastDateOfMonth = new Date(firstDayOfNextMonth - 1);

  return lastDateOfMonth;
}

/**
 * Compares two dates to determine their relationship.
 *
 * @param {string|Date} date1 - The first date to compare.
 * @param {string|Date} date2 - The second date to compare.
 * @returns {string} - "equal", "greater", "lesser", or "invalid" if input is invalid.
 */
export const compareDates = (date1, date2) => {
  if (!Date.parse(date1) || !Date.parse(date2)) {
    return "invalid";
  }

  // Convert date1 and date2 to Date objects.
  const date1Date = new Date(date1);
  const date2Date = new Date(date2);

  // Compare the Date objects.
  if (date1Date.getTime() === date2Date.getTime()) {
    return "equal";
  } else if (date1Date.getTime() > date2Date.getTime()) {
    return "greater";
  } else {
    return "lesser";
  }
};

/**
 * Filters a list of timezones by country-specific criteria.
 *
 * @param {string} country - The country identifier (e.g., "US").
 * @param {Array<string>} timezone - A list of timezone strings.
 * @returns {Array<string>} - A filtered list of timezones matching the country criteria.
 */
export const filterTimezonesByCountry = (country, timezone) => {
  const filter = COUNTRY_TIMEZONES[country?.toUpperCase()];
  if (filter && timezone) {
    return timezone?.filter((timezone) =>
      filter.some((keyword) => timezone.includes(keyword)),
    );
  }
  return timezone ? timezone : [];
};
